import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Loader2, Mail, KeyRound, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PortalForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const [otp, setOtp] = useState("");
  const [otpBusy, setOtpBusy] = useState(false);

  const [resetToken, setResetToken] = useState("");
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [pwdBusy, setPwdBusy] = useState(false);

  const submitEmail = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/portal/auth/password/forgot`, { email: email.trim().toLowerCase() });
      setSent(true);
      toast.success("If that admin email is registered, a reset code is on its way.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(Array.isArray(detail) ? "Enter a valid email" : (detail || "Could not send reset code"));
    } finally {
      setBusy(false);
    }
  };

  const submitOtp = async (e) => {
    e.preventDefault();
    setOtpBusy(true);
    try {
      const r = await axios.post(`${API}/portal/auth/password/verify-otp`, {
        email: email.trim().toLowerCase(),
        code: otp.trim(),
      });
      setResetToken(r.data.reset_token);
      toast.success("Code accepted — choose a new password");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Wrong code — try again");
    } finally {
      setOtpBusy(false);
    }
  };

  const submitPwd = async (e) => {
    e.preventDefault();
    if (pwd.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (pwd !== pwd2) { toast.error("Passwords don't match"); return; }
    setPwdBusy(true);
    try {
      await axios.post(`${API}/portal/auth/password/reset-with-token`, { token: resetToken, new_password: pwd });
      toast.success("Password updated — sign in with your new password");
      setTimeout(() => navigate("/portal/login", { replace: true }), 1200);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not reset password");
    } finally {
      setPwdBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center px-4 py-12">
      <Card className="w-full max-w-md p-8 bg-slate-900 border-slate-800 shadow-2xl" data-testid="portal-forgot-password-page">
        <Link to="/portal/login" className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-white mb-6" data-testid="portal-forgot-back">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to portal sign in
        </Link>

        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
            <KeyRound className="w-5 h-5 text-amber-400" strokeWidth={2} />
          </div>
          <div>
            <h1 className="font-display text-2xl font-bold text-white">Reset portal password</h1>
            <p className="text-xs text-slate-400">For Admins, Analysts and Partners only.</p>
          </div>
        </div>

        {/* Step 1: email */}
        {!sent && (
          <form onSubmit={submitEmail} className="space-y-4" data-testid="portal-forgot-step-email">
            <div>
              <label className="text-xs font-semibold text-slate-300 uppercase tracking-wider">Email address</label>
              <div className="relative mt-1.5">
                <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <Input
                  type="email" autoFocus required
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@admissioncrafts.com"
                  className="h-12 pl-9 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                  data-testid="portal-forgot-email-input"
                />
              </div>
            </div>
            <Button
              type="submit" disabled={busy || !email}
              className="w-full h-12 bg-amber-400 hover:bg-amber-500 text-amber-950 font-semibold"
              data-testid="portal-forgot-submit-button"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {busy ? "Sending…" : "Send reset code"} {!busy && <ArrowRight className="w-4 h-4 ml-2" />}
            </Button>
          </form>
        )}

        {/* Step 2a: OTP */}
        {sent && !resetToken && (
          <>
            <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/30 p-3 text-xs text-emerald-200 mb-4 flex items-start gap-2" data-testid="portal-forgot-sent-banner">
              <ShieldCheck className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>Check your inbox for an email from <strong>AdmissionCrafts</strong>. Use the <strong>6-digit code</strong> below — or click the link inside the email.</span>
            </div>
            <form onSubmit={submitOtp} className="space-y-4" data-testid="portal-forgot-step-otp">
              <div>
                <label className="text-xs font-semibold text-slate-300 uppercase tracking-wider">6-digit code</label>
                <Input
                  inputMode="numeric" pattern="\d{6}" maxLength={6} required autoFocus
                  value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                  placeholder="123456"
                  className="h-12 mt-1.5 text-center text-xl tracking-[0.4em] font-mono bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                  data-testid="portal-forgot-otp-input"
                />
              </div>
              <Button
                type="submit" disabled={otpBusy || otp.length !== 6}
                className="w-full h-12 bg-amber-400 hover:bg-amber-500 text-amber-950 font-semibold"
                data-testid="portal-forgot-otp-submit"
              >
                {otpBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Verify code
              </Button>
              <p className="text-center text-xs text-slate-400">
                Didn't receive it?{" "}
                <button type="button" onClick={() => { setSent(false); setOtp(""); }} className="underline font-semibold text-amber-300">
                  Try a different email
                </button>
              </p>
            </form>
          </>
        )}

        {/* Step 2b: new password */}
        {resetToken && (
          <form onSubmit={submitPwd} className="space-y-4" data-testid="portal-forgot-step-password">
            <div>
              <label className="text-xs font-semibold text-slate-300 uppercase tracking-wider">New password</label>
              <Input
                type="password" required autoFocus minLength={8}
                value={pwd} onChange={(e) => setPwd(e.target.value)}
                placeholder="At least 8 characters"
                className="h-12 mt-1.5 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                data-testid="portal-forgot-new-password-input"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-300 uppercase tracking-wider">Confirm new password</label>
              <Input
                type="password" required minLength={8}
                value={pwd2} onChange={(e) => setPwd2(e.target.value)}
                placeholder="Re-type your new password"
                className="h-12 mt-1.5 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                data-testid="portal-forgot-new-password-confirm"
              />
            </div>
            <Button
              type="submit" disabled={pwdBusy || pwd.length < 8 || pwd !== pwd2}
              className="w-full h-12 bg-amber-400 hover:bg-amber-500 text-amber-950 font-semibold"
              data-testid="portal-forgot-password-submit"
            >
              {pwdBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Save new password
            </Button>
          </form>
        )}
      </Card>
    </div>
  );
}
